import {
  ChevronDown,
  ChevronUp,
  CircleQuestionMark,
  Layers,
  LifeBuoy,
  Search,
  Sparkles,
  Star,
  type LucideIcon,
} from 'lucide-react';
import { useCallback, useMemo, useState } from 'react';

import { Alert } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { APP_META } from '@/config/app';
import { useDocumentTitle } from '@/hooks/use-document-title';
import { normalizeSearch } from '@/lib/search';
import { formatNumber } from '@/lib/utils';

import { FaqFilters } from '../components/faq-filters';
import { FaqItemCard } from '../components/faq-item-card';
import { useFaq } from '../hooks/use-faq';
import { stripHtml } from '../lib/faq-format';

/** Haystack de búsqueda de una pregunta (incluye su respuesta sin etiquetas). */
function buildHaystack(item: {
  pregunta: string;
  respuesta: string;
  categoria_nombre: string;
  tags: readonly string[];
}): string {
  return normalizeSearch(
    [item.pregunta, stripHtml(item.respuesta), item.categoria_nombre, ...item.tags].join(' '),
  );
}

/**
 * Preguntas frecuentes.
 *
 * El contenido (categorías, respuestas y adjuntos) lo administra el panel
 * anterior; aquí sólo se consulta, se filtra y se muestra saneado.
 */
export function FaqPage() {
  useDocumentTitle('Preguntas frecuentes');

  const { data, isPending, isError, refetch, isRefetching } = useFaq();

  const [query, setQuery] = useState('');
  const [categoriaId, setCategoriaId] = useState<number | null>(null);
  const [abiertos, setAbiertos] = useState<readonly number[]>([]);

  // Referencias estables para no invalidar los memos en cada render.
  const items = useMemo(() => data?.items ?? [], [data]);
  const categorias = useMemo(() => data?.categorias ?? [], [data]);

  const haystacks = useMemo(() => new Map(items.map((item) => [item.id, buildHaystack(item)])), [items]);

  const filtrados = useMemo(() => {
    const termino = normalizeSearch(query);

    return items.filter((item) => {
      if (categoriaId !== null && item.categoria_id !== categoriaId) return false;
      if (termino.length < 2) return true;

      return (haystacks.get(item.id) ?? '').includes(termino);
    });
  }, [items, haystacks, query, categoriaId]);

  const toggleItem = useCallback((id: number) => {
    setAbiertos((prev) => (prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]));
  }, []);

  const limpiar = useCallback(() => {
    setQuery('');
    setCategoriaId(null);
  }, []);

  const hayFiltros = query.trim() !== '' || categoriaId !== null;

  return (
    <div className="space-y-6">
      {/* Encabezado */}
      <header>
        <p className="text-xs font-semibold uppercase tracking-wider text-ink-400">Centro de ayuda</p>
        <h1 className="mt-1 flex items-center gap-2 text-2xl font-bold tracking-tight text-ink-900">
          <CircleQuestionMark className="size-6 text-brand-500" aria-hidden="true" />
          Preguntas frecuentes
        </h1>
        <p className="mt-1 text-sm text-ink-500">
          Respuestas rápidas sobre facturación, turnos, configuración y reportes del sistema.
        </p>
      </header>

      {isError ? (
        <Alert variant="error" title="No se pudieron cargar las preguntas frecuentes">
          <div className="flex flex-wrap items-center gap-3">
            <span>Revisa la conexión con el servidor e inténtalo nuevamente.</span>
            <Button size="sm" variant="outline" onClick={() => void refetch()} loading={isRefetching}>
              Reintentar
            </Button>
          </div>
        </Alert>
      ) : null}

      {/* Resumen */}
      {data && !isError ? (
        <div className="grid gap-px overflow-hidden rounded-card border border-ink-100 bg-ink-100 sm:grid-cols-3">
          <ResumenItem
            icon={CircleQuestionMark}
            label="Preguntas publicadas"
            value={formatNumber(data.stats.total_items)}
          />
          <ResumenItem
            icon={Layers}
            label="Categorías"
            value={formatNumber(data.stats.total_categorias)}
          />
          <ResumenItem
            icon={Star}
            label="Marcadas como destacadas"
            value={formatNumber(data.stats.total_destacadas)}
          />
        </div>
      ) : null}

      {/* Filtros */}
      {!isError && (isPending || items.length > 0) ? (
        <FaqFilters
          query={query}
          onQueryChange={setQuery}
          categorias={categorias}
          categoriaId={categoriaId}
          onCategoriaChange={setCategoriaId}
          totalItems={items.length}
        />
      ) : null}

      {/* Carga */}
      {isPending ? (
        <div className="space-y-3">
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton key={index} className="h-20 rounded-card" />
          ))}
        </div>
      ) : null}

      {/* Listado */}
      {!isPending && !isError ? (
        filtrados.length > 0 ? (
          <section aria-label="Preguntas frecuentes" className="space-y-3">
            <div className="flex flex-wrap items-center justify-between gap-3">
              <p className="text-sm text-ink-500">
                {filtrados.length} {filtrados.length === 1 ? 'pregunta' : 'preguntas'}
                {hayFiltros ? ' con los filtros aplicados' : ' disponibles'}
              </p>

              <div className="flex items-center gap-1">
                <Button
                  size="sm"
                  variant="ghost"
                  onClick={() => setAbiertos(filtrados.map((item) => item.id))}
                >
                  <ChevronDown className="size-4" aria-hidden="true" />
                  Expandir todo
                </Button>
                <Button size="sm" variant="ghost" onClick={() => setAbiertos([])}>
                  <ChevronUp className="size-4" aria-hidden="true" />
                  Contraer todo
                </Button>
              </div>
            </div>

            <div className="space-y-3">
              {filtrados.map((item) => (
                <FaqItemCard
                  key={item.id}
                  item={item}
                  abierto={abiertos.includes(item.id)}
                  onToggle={() => toggleItem(item.id)}
                />
              ))}
            </div>
          </section>
        ) : (
          <div className="rounded-card border border-dashed border-ink-200 bg-white/70 px-6 py-14 text-center">
            <Search className="mx-auto size-6 text-ink-300" aria-hidden="true" />
            <h2 className="mt-3 text-base font-semibold text-ink-800">Sin resultados</h2>
            <p className="mx-auto mt-1.5 max-w-md text-sm leading-relaxed text-ink-500">
              {items.length === 0
                ? 'Todavía no hay preguntas frecuentes publicadas.'
                : 'Ninguna pregunta coincide con la búsqueda o la categoría seleccionada.'}
            </p>

            {hayFiltros ? (
              <Button size="sm" variant="outline" className="mt-4" onClick={limpiar}>
                Limpiar filtros
              </Button>
            ) : null}
          </div>
        )
      ) : null}

      {/* Ayuda adicional */}
      {!isPending && !isError ? (
        <Card className="flex flex-col items-start gap-3 border-brand-100 bg-brand-50/50 px-5 py-4 sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-start gap-3">
            <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-white text-brand-600 ring-1 ring-brand-100">
              <LifeBuoy className="size-4.5" aria-hidden="true" />
            </span>
            <div>
              <p className="text-sm font-semibold text-ink-800">¿No encuentras lo que buscas?</p>
              <p className="mt-0.5 text-xs text-ink-500">
                Escríbenos a la mesa de ayuda y te respondemos a la brevedad.
              </p>
            </div>
          </div>

          <a
            href={APP_META.supportUrl}
            target="_blank"
            rel="noreferrer noopener"
            className="inline-flex items-center gap-2 rounded-control bg-brand-600 px-3.5 py-2 text-xs font-semibold text-white transition-colors hover:bg-brand-700"
          >
            <Sparkles className="size-3.5" aria-hidden="true" />
            Contactar soporte
          </a>
        </Card>
      ) : null}
    </div>
  );
}

interface ResumenItemProps {
  icon: LucideIcon;
  label: string;
  value: string;
}

/** Celda del resumen superior del FAQ. */
function ResumenItem({ icon: Icon, label, value }: ResumenItemProps) {
  return (
    <div className="flex items-center gap-3 bg-white px-5 py-4">
      <span className="flex size-9 shrink-0 items-center justify-center rounded-xl bg-brand-50 text-brand-600">
        <Icon className="size-4.5" aria-hidden="true" />
      </span>

      <div className="min-w-0">
        <p className="font-mono text-lg font-bold tracking-tight text-ink-900 tabular-nums">{value}</p>
        <p className="truncate text-xs text-ink-500">{label}</p>
      </div>
    </div>
  );
}
